
import React from 'react'

import moment from 'moment'

import isDate from 'lodash/isDate'

import ExternalLink from 'core/components/external-link'
import InsightsLink from 'core/components/insights-link'

import NpsResolutionStatus from '../nps-resolution/enums/nps-resolution-status'

import Tooltip from '../../../highcharts/components/tooltip/tooltip-wrapper'
import { NumberWithCommas } from '../../../highcharts/helpers/chart-utils'

const dateFormat = value => {
  if (!isDate(value)) {
    return value
  }
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(moment.utc(value).toDate())
}

const addClassName = (header, className) => {
  if (header.className) {
    header.className += ` ${className}`
  } else {
    header.className = className
  }
}

// this will throw an error if it's not a valid url
const parseUrl = url => {
  return new URL(url)
}

const PriceWithCommas = x => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const processTableData = table => {
  const maxCommentLength = 80
  const maxTooltipLength = 1000

  if (table && table.headers && table.data) {
    if (table.dataFilter && typeof table.dataFilter === 'function') {
      table.data = table.dataFilter([...table.data])
    }
    table.headers.forEach(header => {
      header.sortable = true

      table.data.forEach(row => {
        if (table.customRowFormatter && typeof table.customRowFormatter === 'function') {
          table.customRowFormatter(row)
        }
        if (row[header.name] && row[header.name].length > maxCommentLength) {
          row[header.name] = (
            <Tooltip
              title={
                row[header.name] && row[header.name].length > maxTooltipLength
                  ? `${row[header.name].substring(0, maxTooltipLength)}...`
                  : row[header.name]
              }
            >
              <span>{row[header.name].substring(0, maxCommentLength) + '...'}</span>
            </Tooltip>
          )
        }
        if (header.name === 'VatRate') {
          header.type = ''
          header.formatter = val => val * 100 + '%'
        }
      })
      if (header.type === 'number') {
        header.alignRight = true
      }
      if (header.type === 'date' || header.type === 'datetime') {
        header.type = 'mp-custom-date-format'
        header.formatter = value => dateFormat(value)
      }
      if (header.type === 'date') {
        addClassName(header, 'mp-date-column')
      }
      if (header.type === 'insights') {
        header.formatter = (value, row) => {
          if (row.AccountId && row.PartnerId && row.SiteId) {
            return (
              <InsightsLink
                dataElementSuffix={`insights-link-${value}`}
                account={{ id: row.AccountId, partnerId: row.PartnerId }}
                siteId={row.SiteId}
              />
            )
          }
        }
      }

      if (header.type === 'url') {
        header.type = ''
        header.formatter = (value, row) => {
          if (!value) {
            return null
          }

          let hasProtocol = false
          try {
            parseUrl(value)
            hasProtocol = true
          } catch { }

          const to = hasProtocol ? value : `//${value}`
          return (
            <ExternalLink
              linkText={value}
              to={to}
            />
          )
        }
      }

      if (header.type === 'price') {
        if (table.fixedNumbers) {
          header.type = ''
          header.formatter = (value, row) => {
            return (parseFloat(value) < 0 ? '-' : '') + '$' + PriceWithCommas((Number(value) || 0).toFixed(0)).replace('-', '')
          }
        }
        header.alignRight = true
      }

      if (header.alignRight || header.type === 'percentage') {
        addClassName(header, 'mp-column-align-right')
      }

      if (header.type === 'percentage') {
        header.type = ''
        header.formatter = (value, row) => {
          return NumberWithCommas(value + '%')
        }
      }

      if (header.type === 'account') {
        header.formatter = accountId =>
          <ExternalLink
            linkText={accountId}
            to={`/account/${accountId}`}
            rel=''
          />
      }
      // hide this column for now
      if (table.name === 'appuserswithactivecontactsync' && header.name === 'SetupStatus') {
        header.hideColumn = true
      }

      if (table.name && table.name.toLowerCase().includes('nps')) {
        if (header.name === 'Status') {
          header.formatter = val => {
            switch (val) {
              case NpsResolutionStatus.OPEN:
                return 'Open'
              case NpsResolutionStatus.PROCESSED:
                return 'Processed'
              case NpsResolutionStatus.IGNORE:
                return 'Ignored'
            }

            return ''
          }
        }
      }
    })
  }
}

export { processTableData }
